import Container from 'typedi'
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators'
import BadgesService from '../services/badges.service'
import OptionService from '../services/option.service'
import { BadgeSvgResponse, PrintLogEntry, PrintQueueLogMeta, PrintQueueLogRow, RegistrationType } from '@/modules/level-two/models/badges.model'
import Notification from '@/modules/common/services/notification.service'
import ErrorHandlerService from '@/modules/common/services/error-handler.service'

@Module({
  namespaced: true
})
export default class BadgesStore extends VuexModule {
  badges: any[] = []

  registrationTypes: RegistrationType[] = []

  settings: any[] = []

  averyBadgesExport: any = {}

  badgeStats: any = {}

  badgeStatsByHour: any = {}

  participantBadge: any = {}

  recentPrintActivity: PrintLogEntry[] = []

  printLog: PrintQueueLogRow[] = []

  printLogMeta: PrintQueueLogMeta = { total: 0, currentPage: 1, perPage: 25 }

  @Mutation
  SET_BADGES (payload: any) {
    this.badges = payload
  }

  @Mutation
  SET_REGISTRATION_TYPES (payload: any) {
    this.registrationTypes = payload
  }

  @Mutation
  SET_SETTINGS (payload: any) {
    this.settings = payload
  }

  @Mutation
  SET_AVERY_BADGES_EXPORT_RESPONSE (payload: any) {
    this.averyBadgesExport = payload
  }

  @Mutation
  SET_BADGE_STATS (payload: any) {
    this.badgeStats = payload
  }

  @Mutation
  SET_BADGE_STATS_BY_HOUR (payload: any) {
    this.badgeStatsByHour = payload
  }

  @Mutation
  SET_PARTICIPANT_BADGE (payload: any) {
    this.participantBadge = payload
  }

  @Mutation
  SET_RECENT_PRINT_ACTIVITY (payload: PrintLogEntry[]) {
    this.recentPrintActivity = payload
  }

  @Mutation
  SET_PRINT_LOG (payload: { rows: PrintQueueLogRow[]; meta: PrintQueueLogMeta }) {
    this.printLog = payload.rows
    this.printLogMeta = payload.meta
  }

  @Action({ rawError: true })
  async fetchBadges (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).fetchBadges(event_uuid)
    this.context.commit('SET_BADGES', response.data)
    return response
  }

  @Action({ rawError: true })
  async getBadge (payload: any) {
    const { event_uuid, badge_uuid } = payload
    const response = await Container.get(BadgesService).getBadge(event_uuid, badge_uuid)
    return response
  }

  @Action({ rawError: true })
  async createBadge (payload: any) {
    const { event_uuid, data } = payload
    const response = await Container.get(BadgesService).createBadge(event_uuid, data)
    return response
  }

  @Action({ rawError: true })
  async updateBadge (payload: any) {
    const { event_uuid, badge_uuid, data } = payload
    const response = await Container.get(BadgesService).updateBadge(event_uuid, badge_uuid, data)
    return response
  }

  @Action({ rawError: true })
  async deleteBadge (payload: any) {
    const { event_uuid, badge_uuid } = payload
    const response = await Container.get(BadgesService).deleteBadge(event_uuid, badge_uuid)
    return response
  }

  @Action({ rawError: true })
  async getRegistrationTypes (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(OptionService).getOptionGroupByName(event_uuid, 'Registration Types')
    response.data.options.push({ uuid: '_default', name: '_default', visible: 1 })
    this.context.commit('SET_REGISTRATION_TYPES', response.data.options)
    return response
  }

  @Action({ rawError: true })
  async getSettings (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).getSettings(event_uuid)
    this.context.commit('SET_SETTINGS', response.data)
    return response
  }

  @Action({ rawError: true })
  async updateSettings (payload: any) {
    const { event_uuid, data } = payload
    const response = await Container.get(BadgesService).updateSettings(event_uuid, data)
    return response
  }

  @Action({ rawError: true })
  async createAveryBadgesExport (payload: any) {
    const { event_uuid, data } = payload
    const response = await Container.get(BadgesService).createAveryBadgesExport(event_uuid, data)
    const badge_uuid = response.data.uuid
    sessionStorage.setItem('badges_export_uuid', badge_uuid)
    this.context.dispatch('getAveryBadgesExport', { event_uuid, badge_uuid })
    return response
  }

  @Action({ rawError: true })
  async getAveryBadgesExport (payload: any) {
    const { event_uuid, badge_uuid } = payload
    const response = await Container.get(BadgesService).getAveryBadgesExport(event_uuid, badge_uuid)
    if (response.data.status !== 'FINISHED' && response.data.status !== 'FAILED') {
      await new Promise(resolve => setTimeout(resolve, 1000))
      this.context.dispatch('getAveryBadgesExport', payload)
    } else {
      if (response.data.status === 'FAILED') {
        Container.get(Notification).error('There was an error loading the Badges PDF. Please contact support.')
      } else if (response.data.status === 'FINISHED') {
        window.open(response.data.result_data.url, '_blank')
        this.context.commit('SET_AVERY_BADGES_EXPORT_RESPONSE', response.data)
        return response
      }
    }
  }

  @Action({ rawError: true })
  async getLastAveryBadgesExport (payload: any) {
    const { event_uuid, badge_uuid } = payload
    const response = await Container.get(BadgesService).getAveryBadgesExport(event_uuid, badge_uuid)
    if (!response || !response.data || response.data.status === 'FAILED') {
      Container.get(Notification).error('There was an error loading the latest Badges PDF.')
    } else {
      this.context.commit('SET_AVERY_BADGES_EXPORT_RESPONSE', response.data)
      return response
    }
  }

  @Action({ rawError: true })
  async getBadgesPrintByHour (payload: any) {
    const { event_uuid, dates = [] } = payload
    const badgeStatsByHour = {}
    for (const date of dates) {
      const result = await Container.get(BadgesService).getBadgesPrintByHour(event_uuid, date)
      badgeStatsByHour[date] = result.data
    }
    this.context.commit('SET_BADGE_STATS_BY_HOUR', badgeStatsByHour)
  }

  @Action({ rawError: true })
  async getBadgeStats (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).getBadgeStats(event_uuid)
    this.context.commit('SET_BADGE_STATS', response.data)
    return response.data
  }

  @Action({ rawError: true })
  async clearParticipantPrintData (payload: any) {
    const { event_uuid, participant_uuid } = payload
    const response = await Container.get(BadgesService).clearParticipantPrintData(event_uuid, participant_uuid)
    return response
  }

  @Action({ rawError: true })
  async bulkClearParticipantPrintData (payload: any) {
    const { event_uuid, uuids } = payload
    const response = await Container.get(BadgesService).bulkClearParticipantPrintData(event_uuid, uuids)
    return response
  }

  @Action({ rawError: true })
  async getLatestPrintByWorkstation (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).getLatestPrintByWorkstation(event_uuid)
    return response.data
  }

  @Action({ rawError: true })
  async fetchAttendeeBadgeSVG (payload: any): Promise<BadgeSvgResponse | undefined> {
    try {
      const { event_uuid, participant_uuid } = payload
      const response = await Container.get(BadgesService).getAttendeeBadgeSVG(event_uuid, participant_uuid)
      const badgeData = response.data as BadgeSvgResponse
      badgeData.svg = badgeData.svg.substring(badgeData.svg.indexOf('<svg')) ?? ''
      this.context.commit('SET_PARTICIPANT_BADGE', badgeData)
      return badgeData
    } catch (error) {
      Container.get(ErrorHandlerService).error(error)
    }
  }

  @Action({ rawError: true })
  async createPrintLogEntry (payload: any) {
    const { event_uuid, data } = payload
    const response = await Container.get(BadgesService).createPrintLogEntry(event_uuid, data)
    return response
  }

  @Action({ rawError: true })
  async updatePrintLogEntry (payload: any) {
    const { event_uuid, log_uuid, data } = payload
    const response = await Container.get(BadgesService).updatePrintLogEntry(event_uuid, log_uuid, data)
    return response
  }

  @Action({ rawError: true })
  async fetchRecentPrintActivity (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).getPrintLog(event_uuid)
    this.context.commit('SET_RECENT_PRINT_ACTIVITY', response.data)
    return response
  }

  @Action({ rawError: true })
  async fetchPrintLog (payload: any) {
    const { event_uuid, page, per_page, search } = payload
    const response = await Container.get(BadgesService).getPrintQueueLog(event_uuid, { page, per_page, search })
    this.context.commit('SET_PRINT_LOG', {
      rows: response.data.data,
      meta: {
        total: response.data.total,
        currentPage: response.data.current_page,
        perPage: response.data.per_page
      }
    })
    return response
  }

  @Action({ rawError: true })
  async exportPrintLog (payload: any) {
    const { event_uuid } = payload
    const response = await Container.get(BadgesService).exportPrintLog(event_uuid)
    return response
  }
}
